import React, { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Hub } from '@aws-amplify/core'

import decodeJwtToken from 'civic-champs-shared/auth/utils/decodeJwtToken'
import { isSwitchingUser } from '../selectors'
import { resolveAuthenticatedUserWithRetry } from '../sessionUser'

import AuthLoading from './AuthLoading'
import CustomSignIn from './CustomSignIn'
import CustomForgotPassword from './CustomForgotPassword'
import RequireNewPassword from './RequireNewPassword'
import AuthSessionBootstrap from './AuthSessionBootstrap'

const VIEWS = {
  loading: 'loading',
  signIn: 'signIn',
  forgotPassword: 'forgotPassword',
  requireNewPassword: 'requireNewPassword',
  authenticated: 'authenticated',
}

export default function AdminAuthGate({ children }) {
  const [view, setView] = useState(VIEWS.loading)
  const [pendingUser, setPendingUser] = useState(null)
  const [forgotUsername, setForgotUsername] = useState('')
  // Starting or leaving a kiosk signs one Cognito user out and the next one in (Amplify v6
  // rejects signIn otherwise). Both Hub events land while the saga is still working, so render
  // neither the sign in form nor the half-switched app until it says the switch is over.
  const switchingUser = useSelector(isSwitchingUser)

  const resolveSession = useCallback(async () => {
    const searchParams = new URLSearchParams(window.location.search)
    const token = searchParams.get('token') || localStorage.getItem('token')

    if (token) {
      try {
        const payload = decodeJwtToken(token)
        if (payload.exp * 1000 > Date.now()) {
          const stored = localStorage.getItem('token')
          localStorage.setItem('token', token)
          if (token !== stored) {
            localStorage.removeItem('firstLogin')
          }
          if (searchParams.get('token')) {
            searchParams.delete('token')
            const qs = searchParams.toString()
            const next = qs ? `${window.location.pathname}?${qs}` : window.location.pathname
            window.history.replaceState({}, '', next)
          }
          setView(VIEWS.authenticated)
          return
        }
        localStorage.removeItem('token')
        localStorage.removeItem('firstLogin')
      } catch (e) {
        localStorage.removeItem('token')
        localStorage.removeItem('firstLogin')
      }
    }

    const { user } = await resolveAuthenticatedUserWithRetry()
    if (user) {
      setView(VIEWS.authenticated)
      return
    }

    setView(VIEWS.signIn)
  }, [])

  useEffect(() => {
    resolveSession()
  }, [resolveSession])

  useEffect(() => {
    const listener = ({ payload }) => {
      const event = payload?.event
      if (event === 'signIn' || event === 'signedIn') {
        setPendingUser(null)
        setView(VIEWS.authenticated)
      }
      if (event === 'signOut' || event === 'signedOut') {
        setView(VIEWS.signIn)
      }
    }

    const unsubscribe = Hub.listen('auth', listener)
    return () => unsubscribe()
  }, [])

  if (view === VIEWS.loading || switchingUser) {
    return <AuthLoading forceLoading />
  }

  if (view === VIEWS.signIn) {
    return (
      <CustomSignIn
        onForgotPassword={username => {
          setForgotUsername(username || '')
          setView(VIEWS.forgotPassword)
        }}
        onRequireNewPassword={user => {
          setPendingUser(user)
          setView(VIEWS.requireNewPassword)
        }}
        onSignedIn={() => setView(VIEWS.authenticated)}
      />
    )
  }

  if (view === VIEWS.forgotPassword) {
    return <CustomForgotPassword initialUsername={forgotUsername} onBack={() => setView(VIEWS.signIn)} />
  }

  if (view === VIEWS.requireNewPassword && pendingUser) {
    return (
      <RequireNewPassword
        user={pendingUser}
        onComplete={() => setView(VIEWS.authenticated)}
        onCancel={() => {
          setPendingUser(null)
          setView(VIEWS.signIn)
        }}
      />
    )
  }

  if (view === VIEWS.authenticated) {
    return <AuthSessionBootstrap>{children}</AuthSessionBootstrap>
  }

  return <AuthLoading forceLoading />
}
